import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type DateDisplayFormatter = (
  date: Date | string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  fallback?: string
) => string;

/**
 * Provides a memoized date formatting function using the current i18n language.
 *
 * @param defaultOptions - Default `Intl.DateTimeFormatOptions` applied when no per-call override is provided.
 * @param defaultFallback - Default fallback string returned when the provided date is empty or invalid.
 * @returns A formatter function that accepts a date input and optional overrides.
 */
export function useDateDisplay(
  defaultOptions?: Intl.DateTimeFormatOptions,
  defaultFallback?: string
): DateDisplayFormatter {
  const { i18n } = useTranslation();

  const resolvedOptions = useMemo<Intl.DateTimeFormatOptions>(
    () =>
      defaultOptions ?? {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      },
    [defaultOptions]
  );

  const resolvedFallback = useMemo(
    () => defaultFallback ?? '-',
    [defaultFallback]
  );

  const resolvedLocale = useMemo(() => {
    if (i18n.language) {
      return i18n.language;
    }
    if (typeof navigator !== 'undefined' && navigator.language) {
      return navigator.language;
    }
    return 'en-US';
  }, [i18n.language]);

  return useCallback(
    (
      date: Date | string | null | undefined,
      options?: Intl.DateTimeFormatOptions,
      fallback?: string
    ) => {
      if (!date) {
        return fallback ?? resolvedFallback;
      }

      const dateObj = date instanceof Date ? date : new Date(date);
      if (Number.isNaN(dateObj.getTime())) {
        return fallback ?? resolvedFallback;
      }

      try {
        return new Intl.DateTimeFormat(
          resolvedLocale,
          options ?? resolvedOptions
        ).format(dateObj);
      } catch (error) {
        console.error('Error formatting date with locale', error);
        return fallback ?? resolvedFallback;
      }
    },
    [resolvedLocale, resolvedOptions, resolvedFallback]
  );
}
